import { AxiosInstance, AxiosPromise } from 'axios';
import { fromArrayToCSV } from '@trukt/portal-utils/fromArrayToCSV';
import { PaginatedQueryParams } from '@trukt/types/PaginatedQueryParams';
import { PaginatedResults } from '@trukt/types/PaginatedResults';
import { JobInspectionTypeLiterals } from '@trukt/types/Job';
import {
  InspectionCreateNotesRequest,
  InspectionCreateNotesResponse,
  InspectionCreateRequest,
  InspectionCreateResponse,
  InspectionGetNotesRequest,
  InspectionGetNotesResponse,
  InspectionMarkCompletedRequest,
  InspectionMarkCompletedResponse,
  InspectionSearchRequest,
  InspectionSearchResponse,
  InspectionSearchResult,
  InspectionServiceEndpoints,
} from './InspectionService.types';

const createInspectionSearchResult = (
  id: string,
  values: Partial<InspectionSearchResult>,
): InspectionSearchResult => ({
  commodityId: '0feb2e83-947c-41a9-81a7-2dbbbc8ecb22',
  dateCreated: '2023-01-20T22:32:24.981Z',
  id,
  jobId: '8075cb47-aba0-47d3-bc46-3433d60ba73a',
  orderId: 'dee70bdc-ee59-47a0-9f0a-82f86f390343',
  status: 'completed',
  stopId: 'fa6ee2cd-c9b7-49c6-95de-5dfffdf57ef0',
  ...values,
});

export type InspectionTypeSearchRequest = PaginatedQueryParams;
export type InspectionTypeSearchResponse = PaginatedResults<{
  name: JobInspectionTypeLiterals;
  isDefault: boolean;
}>;

export class InspectionService implements InspectionServiceEndpoints {
  instance: AxiosInstance;
  constructor(instance: AxiosInstance) {
    this.instance = instance;
  }

  create(request: InspectionCreateRequest): AxiosPromise<InspectionCreateResponse> {
    return this.instance.post('/inspections', request.body);
  }

  search(request: InspectionSearchRequest): AxiosPromise<InspectionSearchResponse> {
    // TODO: remove this mock response when BE endpoint is returning valid data
    // CODE TO BE REMOVED BEGIN
    const results: InspectionSearchResult[] = [];

    if (request.queryParams) {
      const { commodities, jobs, stops } = request.queryParams;

      const firstJob = jobs && jobs.length > 0 ? jobs[0] : undefined;

      const firstStop = stops && stops.length > 0 ? stops[0] : undefined;

      const secondStop = stops && stops.length > 1 ? stops[1] : undefined;

      if (commodities && commodities.length > 1) {
        // Display Inspection Completed for the first record
        const firstCommodity = commodities[0];
        if (firstCommodity)
          results.push(
            createInspectionSearchResult('1', {
              commodityId: firstCommodity,
              jobId: firstJob,
              stopId: firstStop,
              status: 'completed',
            }),
          );

        // Display Inspection Completed for the first record
        const secondCommodity = commodities[1];
        if (secondCommodity)
          results.push(
            createInspectionSearchResult('2', {
              commodityId: secondCommodity,
              jobId: firstJob,
              stopId: secondStop,
              status: 'in-progress',
            }),
          );
      }
    }

    const response: InspectionSearchResponse = {
      page: 1,
      rows: 2,
      total: 2,
      results,
    };

    return new Promise((resolve) => {
      setTimeout(() => {
        // @ts-expect-error
        resolve({ data: response });
      }, 1000);
    }) as AxiosPromise<InspectionSearchResponse>;
    // CODE TO BE REMOVED END

    const { jobs, stops, commodities, orders, ...rest } = request.queryParams;

    return this.instance.post(`/inspections`, {
      params: {
        jobs: fromArrayToCSV(jobs),
        stops: fromArrayToCSV(stops),
        commodities: fromArrayToCSV(commodities),
        orders: fromArrayToCSV(orders),
        ...rest,
      },
    });
  }

  markCompleted({
    id,
  }: InspectionMarkCompletedRequest): AxiosPromise<InspectionMarkCompletedResponse> {
    return this.instance.put(`/inspections/${id}/completed`);
  }

  getNotes(request: InspectionGetNotesRequest): AxiosPromise<InspectionGetNotesResponse> {
    return this.instance.get<InspectionGetNotesResponse>(
      `/inspections/${request.routeParams.id}/notes`,
    );
  }

  createNotes(request: InspectionCreateNotesRequest): AxiosPromise<InspectionCreateNotesResponse> {
    return this.instance.post<InspectionCreateNotesResponse>(
      `/inspections/${request.routeParams.id}/notes`,
      request.body,
    );
  }

  searchTypes(request: InspectionTypeSearchRequest) {
    return this.instance.get<InspectionTypeSearchResponse>('/inspections/types', {
      params: request,
    });
  }
}
